const sections = [
  {
    id: "how-to-request",
    title: "How to Request Data Deletion",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          You may request deletion of your data by using one of the following methods:
        </p>

        <div className="mt-5 rounded-2xl border border-[rgba(109,74,255,0.1)] bg-white/70 p-5 backdrop-blur-sm">
          <p className="text-sm font-black text-[#101522]">Option 1: Email Request</p>
          <p className="mt-2 text-sm text-[#667085]">Send an email to:</p>
          <a
            href="mailto:support@doclift.ai"
            className="mt-1 inline-block text-sm font-bold text-[#6d4aff] hover:underline"
          >
            support@doclift.ai
          </a>
          <p className="mt-3 text-sm text-[#667085]">Please include the following information:</p>
          <ul className="mt-3 grid gap-2">
            {[
              "Your name",
              "Your registered email address",
              "Your WhatsApp phone number (if applicable)",
              "Your organization or tenant name",
              "A brief description of your deletion request",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#6d4aff]" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 rounded-2xl border border-[rgba(19,166,111,0.12)] bg-white/70 p-5 backdrop-blur-sm">
          <p className="text-sm font-black text-[#101522]">Option 2: In-Application Request</p>
          <p className="mt-2 text-sm text-[#667085]">
            If you are a registered DocLift user, navigate to:
          </p>
          <p className="mt-2 text-sm font-bold text-[#374151]">
            Settings → Privacy → Request Data Deletion
          </p>
          <p className="mt-1 text-sm text-[#667085]">Submit the request using the available form.</p>
        </div>
      </>
    ),
  },
  {
    id: "what-will-be-deleted",
    title: "What Data Will Be Deleted",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          Upon successful verification of your request, we will permanently delete, where applicable:
        </p>
        <ul className="mt-4 grid gap-2">
          {[
            "User profile information",
            "WhatsApp integration information",
            "WhatsApp conversation history stored by DocLift",
            "Messages stored within the DocLift platform",
            "Customer contact information managed by the tenant",
            "Authentication tokens associated with Meta integrations",
            "Cached application data related to your account",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#13a66f]" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-[#667085]">
          Please note that certain records may be retained where required by applicable laws,
          regulations, fraud prevention requirements, or legitimate business obligations.
        </p>
      </>
    ),
  },
  {
    id: "processing-time",
    title: "Processing Time",
    content: (
      <p className="text-[#667085] leading-7">
        Verified deletion requests are typically processed within{" "}
        <span className="font-bold text-[#374151]">30 days</span>. Once completed, your data will
        be permanently removed from our production systems unless retention is legally required.
      </p>
    ),
  },
  {
    id: "third-party",
    title: "Third-Party Services",
    content: (
      <p className="text-[#667085] leading-7">
        DocLift integrates with Meta's WhatsApp Business Platform. Deleting your data from DocLift
        removes information stored within our platform. Data maintained by Meta or other third-party
        providers remains subject to their respective privacy policies and data retention practices.
        For more information about how Meta collects, processes, and retains your information, 
        please visit the <a href="https://www.meta.com/privacy/" className="text-[#6d4aff] hover:underline">
              Meta Privacy Policy.
            </a>
        
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact Us",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          If you have any questions regarding data deletion or privacy, please contact us at:
        </p>
        <div className="mt-5 grid gap-2 rounded-2xl border border-[rgba(109,74,255,0.12)] bg-white/70 p-5 text-sm backdrop-blur-sm">
          {[
            ["Company", "Orryworx Pvt. Ltd."],
            ["Application", "DocLift"],
          ].map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <span className="w-24 font-semibold text-[#374151]">{label}</span>
              <span className="text-[#667085]">{value}</span>
            </div>
          ))}
          <div className="flex gap-2">
            <span className="w-24 font-semibold text-[#374151]">Email</span>
            <a href="mailto:support@doclift.ai" className="text-[#6d4aff] hover:underline">
              support@doclift.ai
            </a>
          </div>
        </div>
        <p className="mt-4 text-sm text-[#667085]">
          We are committed to protecting your privacy and handling your data responsibly.
        </p>
      </>
    ),
  },
];

const DataDeletion = () => {
  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(circle at 86% 18%, rgba(109,74,255,0.08), transparent 26%), radial-gradient(circle at 16% 78%, rgba(19,166,111,0.07), transparent 28%), linear-gradient(180deg,#ffffff 0%,#fbfaff 52%,#f8fff9 100%)",
      }}
    >
      {/* Hero banner */}
      <div className="border-b border-[rgba(109,74,255,0.1)] bg-white/60 backdrop-blur-sm">
        <div className="mx-auto max-w-[1100px] px-5 py-14 sm:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(109,74,255,0.18)] bg-[rgba(109,74,255,0.07)] px-4 py-1.5 text-xs font-bold text-[#6d4aff]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#6d4aff]" />
            Legal
          </span>
          <h1 className="mt-5 text-4xl font-black tracking-tight text-[#101522] sm:text-5xl">
            Data Deletion Instructions
          </h1>
          <p className="mt-3 text-sm text-[#667085]">
            Last Updated: <span className="font-semibold text-[#374151]">July 21, 2026</span>
          </p>
          <p className="mt-5 max-w-2xl text-base leading-7 text-[#667085]">
            At <span className="font-semibold text-[#374151]">DocLift</span>, we respect your
            privacy and your right to control your personal data. If you would like to request the
            deletion of your data associated with our application or WhatsApp integration, please
            follow the steps below.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-[1100px] px-5 py-12 sm:px-8 lg:grid lg:grid-cols-[220px_1fr] lg:gap-12">
        {/* Sticky TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-[rgba(109,74,255,0.1)] bg-white/70 p-5 backdrop-blur-sm">
            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#6d4aff]">Contents</p>
            <nav className="grid gap-1">
              {sections.map((s, i) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-semibold text-[#667085] transition hover:bg-[rgba(109,74,255,0.06)] hover:text-[#6d4aff]"
                >
                  <span className="text-[10px] font-bold text-[#6d4aff] opacity-60">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Sections */}
        <main className="grid gap-6">
          {sections.map((s, i) => (
            <section
              key={s.id}
              id={s.id}
              className="rounded-2xl border border-[rgba(109,74,255,0.1)] bg-white/70 p-6 backdrop-blur-sm transition hover:border-[rgba(109,74,255,0.2)] hover:shadow-[0_18px_52px_rgba(82,63,181,0.08)] sm:p-8"
            >
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[rgba(109,74,255,0.08)] text-xs font-black text-[#6d4aff]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="text-lg font-black text-[#101522]">{s.title}</h2>
              </div>
              {s.content}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default DataDeletion;
